import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-00f2df28"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "ticket-item-availability" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("hr", { class: "ticket-item-availability__section-divider" }, null, -1))
const _hoisted_3 = { class: "ticket-item-availability__section-wrapper" }
const _hoisted_4 = { class: "ticket-item-availability__header" }
const _hoisted_5 = { class: "body-1-semi" }
const _hoisted_6 = { class: "ticket-item-availability__quantity-wrapper" }
const _hoisted_7 = {
  key: 0,
  class: "ticket-item-availability__section"
}
const _hoisted_8 = { class: "ticket-item-availability__sub-section ticket-item-availability__sub-section-start" }
const _hoisted_9 = { class: "ticket-item-availability__choice-section" }
const _hoisted_10 = { class: "ticket-item-availability__choice-wrapper" }
const _hoisted_11 = { class: "ticket-item-availability__sub-section ticket-item-availability__sub-section-duration" }
const _hoisted_12 = { class: "ticket-item-availability__choice-section" }
const _hoisted_13 = { class: "ticket-item-availability__choice-wrapper" }
const _hoisted_14 = { key: 1 }
const _hoisted_15 = { class: "ticket-item-availability__validity-disclaimer" }
const _hoisted_16 = { key: 0 }
const _hoisted_17 = { key: 0 }
const _hoisted_18 = { key: 1 }

import { computed, PropType, ref, watch } from 'vue'
import {
  ITicketItemDateAvailability,
  ServiceTicketType,
} from '@/models/service/serviceTicket.model'
import { differenceInMinutes, format } from 'date-fns'
import {
  formatMinutesToDuration,
  formatSecondsToDuration,
  formatServerDateTime,
} from '@/utils/formatters'
import { DurationMinutes } from '@/models/shared.model'
import BaseNumberIncrementer from '@/components/BaseNumberIncrementer.vue'
import BaseIcon from '@/components/BaseIcon.vue'
import { ValidationErrorsForOneObject } from '@/models/error.model'
import isSameMinute from 'date-fns/isSameMinute'
import { ALERT_TYPES } from '@/enums/components'


import {
  ITicketItemDateAvailabilityTimeslot,
  ITicketServiceItemPriceInformationBodyItem,
} from '@/models/service/serviceTicket.model'

interface ITimeslotWrapper {
  timeslot: ITicketItemDateAvailabilityTimeslot
  start: Date
  end: Date
  durationMinutes: number
}

export default /*#__PURE__*/_defineComponent({
  __name: 'TicketResultCardItem',
  props: {
  ticketItem: {
    type: Object as PropType<ITicketItemDateAvailability | null>,
    required: true,
  },
  selection: {
    type: Object as PropType<ITicketServiceItemPriceInformationBodyItem | null>,
    default: null,
  },
  disabled: Boolean,
  errors: {
    type: Object as PropType<ValidationErrorsForOneObject | undefined>,
    default: undefined,
  },
},
  emits: ["update:selection"],
  setup(__props, { emit }: { emit: ({
  (
    e: 'update:selection',
    newSelection: ITicketServiceItemPriceInformationBodyItem | null,
  )
}), expose: any, slots: any, attrs: any }) {

const props = __props





const selectedQuantity = ref<number>(1)
const selectedStart = ref<Date | null>(null)
const selectedDuration = ref<DurationMinutes | null>(null)

const selectedTimeslot = computed<ITimeslotWrapper | null>(() => {
  if (!selectedStart.value || !selectedDuration.value) {
    return null
  }

  return (
    extendedTimeslots.value.filter((timeslotWrapper) => {
      const isStartMatch = isSameMinute(
        timeslotWrapper.start,
        selectedStart.value,
      )
      const isDurationMatch =
        timeslotWrapper.durationMinutes === selectedDuration.value
      return isStartMatch && isDurationMatch
    })?.[0] ?? null
  )
})

const extendedTimeslots = computed<ITimeslotWrapper[]>(() => {
  return props.ticketItem?.timeslots.map((timeslot) => {
    const start = new Date(timeslot.start)
    const end = new Date(timeslot.end)
    return {
      timeslot: timeslot,
      start,
      end,
      durationMinutes: differenceInMinutes(end, start),
    }
  })
})

const maxQuantity = computed<number>(() => {
  const quantities = extendedTimeslots.value.map(
    (timeslotWrapper) => timeslotWrapper.timeslot.available_quantity,
  )
  return Math.max(...quantities, 0)
})

const hasNoOptions = computed<boolean>(() => {
  return extendedTimeslots.value.length === 0
})

const startOptions = computed<Date[]>(() => {
  const startDates = extendedTimeslots.value
    .filter((timeslotWrapper) => {
      if (
        selectedDuration.value &&
        timeslotWrapper.durationMinutes !== selectedDuration.value
      ) {
        return false
      }
      return (
        timeslotWrapper.timeslot.available_quantity >= selectedQuantity.value
      )
    })
    .map((timeslotWrapper) => timeslotWrapper.start)

  const coveredTimestamps = new Set()
  const uniqueDates = []
  for (const date of startDates) {
    const timestamp = date.getTime()
    if (coveredTimestamps.has(timestamp)) {
      continue
    }
    coveredTimestamps.add(timestamp)
    uniqueDates.push(date)
  }
  return uniqueDates.sort((a, b) => a - b)
})

const durationOptions = computed<DurationMinutes[]>(() => {
  const durations = extendedTimeslots.value
    .filter((timeslotWrapper) => {
      if (
        selectedStart.value &&
        timeslotWrapper.start.getTime() !== selectedStart.value.getTime()
      ) {
        return false
      }
      return (
        timeslotWrapper.timeslot.available_quantity >= selectedQuantity.value
      )
    })
    .map((timeslotWrapper) => timeslotWrapper.durationMinutes)
  const uniqueDurations = Array.from(new Set(durations))
  return uniqueDurations.sort((a, b) => a - b)
})

watch(
  () => props.selection,
  () => {
    if (props.selection) {
      selectedQuantity.value = props.selection.quantity
      selectedStart.value = new Date(props.selection.start)
      selectedDuration.value = differenceInMinutes(
        new Date(props.selection.end),
        new Date(props.selection.start),
      )
    } else {
      //selectedQuantity.value = 1
      selectedStart.value = null
      selectedDuration.value = null
    }
  },
  {
    immediate: true,
  },
)

watch([startOptions, durationOptions], () => {
  if (selectedStart.value) {
    let matchingTimeFound = false
    for (const startOption of startOptions.value) {
      const isHoursMatch =
        startOption.getHours() === selectedStart.value.getHours()
      const isMinutesMatch =
        startOption.getMinutes() === selectedStart.value.getMinutes()
      const isDayTimeMatch = isHoursMatch && isMinutesMatch
      if (isDayTimeMatch) {
        matchingTimeFound = true
        selectedStart.value = startOption
        break
      }
    }
    if (!matchingTimeFound) {
      selectedStart.value = null
    }
  }
  if (
    selectedDuration.value &&
    !durationOptions.value.includes(selectedDuration.value)
  ) {
    selectedDuration.value = null
  }
})

watch([selectedTimeslot, selectedQuantity], () => {
  if (selectedTimeslot.value) {
    emit('update:selection', {
      ticket_item: props.ticketItem.ticket_item,
      quantity: selectedQuantity.value,
      start: formatServerDateTime(selectedTimeslot.value.start),
      end: formatServerDateTime(selectedTimeslot.value.end),
    })
  } else {
    emit('update:selection', null)
  }
})

return (_ctx: any,_cache: any) => {
  const _component_BaseTag = _resolveComponent("BaseTag")!
  const _component_AlertBanner = _resolveComponent("AlertBanner")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("span", _hoisted_5, _toDisplayString(__props.ticketItem.name), 1),
        _createVNode(BaseNumberIncrementer, {
          amount: selectedQuantity.value,
          errors: __props.errors?.quantity,
          "min-amount": 1,
          "max-amount": _unref(maxQuantity),
          disabled: __props.disabled,
          class: "ticket-item-availability__input",
          onClickMinus: _cache[0] || (_cache[0] = _withModifiers(() => (selectedQuantity.value -= 1), ["prevent"])),
          onClickPlus: _cache[1] || (_cache[1] = _withModifiers(() => (selectedQuantity.value += 1), ["prevent"]))
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_6, [
              _createVNode(BaseIcon, {
                size: 20,
                icon: "user"
              }),
              _createTextVNode("x " + _toDisplayString(selectedQuantity.value), 1)
            ])
          ]),
          _: 1
        }, 8, ["amount", "errors", "max-amount", "disabled"])
      ]),
      (!_unref(hasNoOptions))
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("h6", null, _toDisplayString(_ctx.$t('start')), 1),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(startOptions), (dateTime) => {
                    return (_openBlock(), _createBlock(_component_BaseTag, {
                      key: dateTime,
                      label: _unref(format)(dateTime, 'HH:mm'),
                      "hide-close": "",
                      disabled: __props.disabled,
                      class: _normalizeClass(["ticket-item-availability__choice", [
                  selectedStart.value === dateTime &&
                    'ticket-item-availability__choice-selected',
                ]]),
                      onClick: ($event: any) => (
                  selectedStart.value = selectedStart.value !== dateTime ? dateTime : null
                )
                    }, null, 8, ["label", "disabled", "class", "onClick"]))
                  }), 128))
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("h6", null, _toDisplayString(_ctx.$t('duration')), 1),
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("div", _hoisted_13, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(durationOptions), (duration, durationIndex) => {
                    return (_openBlock(), _createBlock(_component_BaseTag, {
                      key: durationIndex,
                      label: _unref(formatMinutesToDuration)(duration),
                      "hide-close": "",
                      disabled: __props.disabled,
                      class: _normalizeClass(["ticket-item-availability__choice", [
                  selectedDuration.value === duration &&
                    'ticket-item-availability__choice-selected',
                ]]),
                      onClick: ($event: any) => (
                  selectedDuration.value =
                    selectedDuration.value !== duration ? duration : null
                )
                    }, null, 8, ["label", "disabled", "class", "onClick"]))
                  }), 128))
                ])
              ])
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_14, [
            _createElementVNode("span", null, _toDisplayString(_ctx.$t('noOptions')), 1)
          ])),
      _createElementVNode("div", _hoisted_15, [
        (__props.ticketItem.type === _unref(ServiceTicketType).TIMESLOT)
          ? (_openBlock(), _createElementBlock("div", _hoisted_16, _toDisplayString(_ctx.$t('ticketValidity.disclaimer.validDuringWholeTimeslot')), 1))
          : _createCommentVNode("", true),
        (__props.ticketItem.type === _unref(ServiceTicketType).DURATION)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (__props.ticketItem.is_duration_limited)
                ? (_openBlock(), _createElementBlock("div", _hoisted_17, _toDisplayString(_ctx.$t('ticketValidity.disclaimer.validUntilIsLimitedByEndDynamic', {
                duration: _unref(formatSecondsToDuration)(__props.ticketItem.duration),
              })), 1))
                : (_openBlock(), _createElementBlock("div", _hoisted_18, _toDisplayString(_ctx.$t('ticketValidity.disclaimer.validUntilNotLimitedByEndDynamic', {
                duration: _unref(formatSecondsToDuration)(__props.ticketItem.duration),
              })), 1))
            ], 64))
          : _createCommentVNode("", true)
      ]),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.errors?.non_field_errors, (error) => {
        return (_openBlock(), _createBlock(_component_AlertBanner, {
          key: error,
          text: error,
          "alert-type": _unref(ALERT_TYPES).DANGER,
          class: "ticket-card__alert"
        }, null, 8, ["text", "alert-type"]))
      }), 128))
    ])
  ]))
}
}

})